import type { BillingPlanKey } from '@sparklayer-io/spark-dash-core/context/env-context';
import {
  type Environment,
  isLive,
  isManagedInPlatformAdmin,
  isShopify,
} from '@sparklayer-io/spark-dash-pages';
import { isAxiosError } from 'axios';
import { defineStore } from 'pinia';
import type { Platform } from '@/features';
import { holdForNavigation } from '@/lib/loadingScreen';
import notificationStore from '@/store/modules/notifications';
import useSettingsStore from '@/store/modules/settings';
import api from '@/utils/api';

export type Env = {
  id: number;
  team_id: number;
  environment: Environment;
  db_created: number;
  platform: Platform;
  store_id: string;
  onboarding_platform: boolean;
  onboarding_api: boolean;
  onboarding_sync: boolean;
  onboarding_plus: boolean;
  onboarding_settings: boolean;
  onboarding_frontend: boolean;
  onboarding_launch: boolean;
  created_at: string;
  updated_at: string;
  platform_removed_at: string | null;
  sparkjs_version: string;
  team: {
    id: number;
    name: string;
    is_partner: boolean;
    slugged_name: string;
    website: string;
    app_registration_source: string;
    billing_plan_name: string | null;
    shopify_recurring_trial_ends_at: string | null;
    shopify_enterprise_billing: boolean;
    shopify_enterprise_planned_go_live: string | null;
    billing_custom_price: number | null;
    orders_allowed: number | null;
    shopify_enterprise_billing_no_of_teams_allowed: number | null;
    billing_linked_team_id: number | null;
    shopify_enterprise_billing_completed: number;
    sales_agents_allowed: number | null;
    billing_duration: string | null;
    billing_provider: string;
    advanced_features_enabled: boolean;
    enterprise_features_enabled: boolean;
    billing_plan_data: Record<string, unknown> | null;
    billing_linked_site_id: number | null;
    is_scheduled_for_deletion: boolean;
  };
  billing: {
    has_active_plan: boolean;
    trial_ends_at: string;
    trial_ends_in_days: number;
    has_trial_expired: boolean;
    is_enterprise_billing: boolean;
    provider: string;
    plan_name: string;
    plan_key: BillingPlanKey;
    price_cents: number;
    sales_agents_allowed: number;
    orders_allowed: number;
    teams_allowed: number | null;
    is_deprecated: boolean | null;
    duration: string;
    is_advanced_features_enabled: boolean;
    has_pending_plan: boolean;
    allow_change_plan: boolean;
    pending_plan: {
      billing_provider_name?: string;
      duration: string;
      billing_plan: {
        public_name: string;
        price: number;
        orders_allowed: number | null;
        sales_agents_allowed: number | null;
      };
    } | null;
  };
  connected_platform: Platform;
};

type State = {
  siteId: string | null;
  siteEnv: string | null;
  env: Env | null;
  /** Set while a load is in flight, so the app can cover the page being left behind. */
  loading: boolean;
};

/** What to do when a site rejects the environment asked for. Absent means fail as asked. */
type EnvFallback =
  /** Keep whichever environment loads - the one asked for was ours to pick. */
  | 'any'
  /** Keep it only if the site turns out to be a partner's; they have no live environment. */
  | 'partner';

type LoadEnvOptions = {
  /** Skip the loading screen, for a caller already showing one of its own. */
  silent?: boolean;
  fallback?: EnvFallback;
};

/** The env for a site, plus the status of the attempt so the caller can tell why it failed. */
async function fetchEnv(
  siteId: string | null,
  siteEnv: string | null,
): Promise<{ env: Env | null; status?: number }> {
  try {
    const { data } = await api.get<Env>(`/${siteId}/${siteEnv}`);
    return { env: data };
  } catch (err) {
    return { env: null, status: isAxiosError(err) ? err.response?.status : undefined };
  }
}

/** The site itself was rejected, as opposed to the request failing on the way there. */
function isSiteRejected(status?: number): boolean {
  return status === 403 || status === 404;
}

export default defineStore('env', {
  state: (): State => ({
    siteId: null,
    siteEnv: null,
    env: null,
    loading: false,
  }),
  getters: {
    isLoaded: (state) => state.env !== null,
    /** Identifies the loaded site, for keying views that must not outlive it. */
    siteScope: (state) => `${state.siteId}:${state.siteEnv}`,
    isLive: (state) => isLive(state.env?.environment),
    isSiteReady: (state) => !!state.env?.db_created,
    team: (state) => state.env?.team,
    billing: (state) => state.env?.billing,
    acronymTeamName: (state) =>
      state.env?.team.name.match(/\b\w/g)?.join('').toUpperCase().substring(0, 2),
    isAdvancedFeaturesEnabled: (state) => state.env?.billing?.is_advanced_features_enabled || false,
    isPartner: (state) => state.env?.team?.is_partner || false,
    isManagedInPlatformAdmin: (state) =>
      isManagedInPlatformAdmin(state.env?.connected_platform, state.env?.created_at),
    plan: (state) => state.env?.billing?.plan_key,
    environment: (state) => state.env?.environment,
    // @todo long term remove the below but the backend doesn't care about plus or not
    // but the dashboard does
    syncLogPlatform(state): Platform | undefined {
      return this.isShopifyPlus ? 'shopify' : state.env?.platform;
    },
    // `isPlatform('shopify')` matches Shopify Plus too, since the dashboard almost always wants
    // the whole Shopify family. Reach for the two tier getters below only when the distinction
    // actually matters (billing tiers, platform branding).
    isPlatform: (state) => (platformString: string) =>
      platformString === 'shopify'
        ? isShopify(state.env?.connected_platform)
        : state.env?.connected_platform === platformString,
    isShopifyPlus: (state) => state.env?.platform === 'shopify_plus',
    isShopifyStandard: (state) => state.env?.platform === 'shopify',
    platformName: (state) => () => {
      if (!state.env?.connected_platform) {
        return '';
      }
      if (isShopify(state.env.connected_platform)) {
        return 'Shopify';
      }
      switch (state.env.connected_platform) {
        case 'magento':
          return 'Magento';
        case 'wix':
          return 'Wix';
        case 'woocommerce':
          return 'WooCommerce';
        case 'bigcommerce':
          return 'BigCommerce';
        default:
          return state.env.connected_platform;
      }
    },
  },
  actions: {
    setData({ siteId, siteEnv, env }: Pick<State, 'siteId' | 'siteEnv' | 'env'>) {
      this.siteId = siteId;
      this.siteEnv = siteEnv;
      this.env = env;
    },
    async loadEnv(input?: { siteId: string; siteEnv: string }, options: LoadEnvOptions = {}) {
      const settingsStore = useSettingsStore();

      let { siteId, siteEnv } = this;
      if (input?.siteId) {
        siteId = input.siteId;
      }
      if (input?.siteEnv) {
        siteEnv = input.siteEnv;
      }

      // Avoid loading environment if site id and site env have not changed. `isLoaded` is part of
      // the check because a failed load keeps the site it was for, and that has to be retried
      // rather than skipped.
      if (
        input !== undefined &&
        this.isLoaded &&
        this.siteId === siteId &&
        this.siteEnv === siteEnv
      ) {
        return;
      }

      if (!options.silent) {
        this.loading = true;
      }

      try {
        const attempt = await fetchEnv(siteId, siteEnv);
        let env = attempt.env;

        // Partner sites only have a test environment, so asking one for live has to fall back
        // rather than dead-end on the getting ready screen. Only when the site itself rejected
        // the environment - a request that failed on the way must not move the user elsewhere.
        if (env === null && options.fallback && isSiteRejected(attempt.status)) {
          const otherEnv = siteEnv === 'live' ? 'test' : 'live';
          const fallback = await fetchEnv(siteId, otherEnv);

          // `team.is_partner` is the backend's own answer for this exact site, so a `partner`
          // fallback cannot move a site that isn't one - it fails as asked instead.
          if (fallback.env && (options.fallback === 'any' || fallback.env.team.is_partner)) {
            siteEnv = otherEnv;
            env = fallback.env;
          }
        }

        if (env === null) {
          // Hold on to the site that was asked for, but not to the env we had: whether the load
          // was refused or just failed, the team we hold is no longer known to match the site in
          // the url, and leaving it in place puts one team's name, billing and store id on screen
          // under another team's site id. Without an env the router sends the user to
          // `/getting-ready`, which retries the load.
          this.setData({ siteId, siteEnv, env: null });
          settingsStore.$reset();

          notificationStore.notify(
            'Error',
            `Sorry an error has occurred, please try again. If you're still having issues, please contact our support team.`,
            'error',
          );
          return;
        }

        this.setData({ siteId, siteEnv, env });

        if (this.isSiteReady) {
          await settingsStore.loadSettings(env);
        }
      } finally {
        if (!options.silent) {
          // Hold the screen up first, so it never blinks out between the two.
          holdForNavigation();
          this.loading = false;
        }
      }
    },
  },
});
